import { useMsal } from '@azure/msal-react';
import { notification } from 'antd';
import { useState } from 'react';

import {
	SapCodeQuotaAreaDto,
	SapCodeQuotaDto,
	SapCodeQuotaImportResultDto,
	SapCodeQuotaWriteDto,
} from '../../api-client';
import { QuotaApiClient } from '../../providers/api-provider';
import { saveBlob, today } from '../../utils/save-blob';

export const useQuotasAdmin = () => {
	const { instance } = useMsal();

	const [quotas, setQuotas] = useState<SapCodeQuotaDto[]>([]);
	const [quotasTotal, setQuotasTotal] = useState<number>(0);
	const [quotasLoading, setQuotasLoading] = useState(false);

	const [areas, setAreas] = useState<SapCodeQuotaAreaDto[]>([]);
	const [areasLoading, setAreasLoading] = useState(false);

	const [savingQuota, setSavingQuota] = useState(false);
	const [removingQuota, setRemovingQuota] = useState(false);
	const [resettingQuota, setResettingQuota] = useState(false);
	const [exportingQuotas, setExportingQuotas] = useState(false);
	const [importingQuotas, setImportingQuotas] = useState(false);

	const [importResult, setImportResult] =
		useState<SapCodeQuotaImportResultDto | null>(null);

	const handleQuotas = async (
		responsabilityAreaCode?: string,
		sapCode?: string,
	) => {
		setQuotasLoading(true);
		try {
			const quotaApi = await QuotaApiClient(instance);
			const response = await quotaApi.searchSapCodeQuotas({
				responsabilityAreaCode,
				sapCode,
			});

			setQuotas(response.data?.items ?? []);
			setQuotasTotal(response.data?.totalCount ?? 0);
		} catch {
			setQuotas([]);
			setQuotasTotal(0);
			notification.error({
				message: 'Unable to retrieve quotas!',
				description: 'Unable to retrieve the sap code quotas.',
				duration: 2,
			});
		} finally {
			setQuotasLoading(false);
		}
	};

	const handleAreas = async () => {
		setAreasLoading(true);
		try {
			const quotaApi = await QuotaApiClient(instance);
			const response = await quotaApi.getQuotaAreas();
			setAreas(response.data ?? []);
		} catch {
			setAreas([]);
			notification.error({
				message: 'Unable to retrieve areas!',
				description: 'Unable to retrieve the responsability areas.',
				duration: 2,
			});
		} finally {
			setAreasLoading(false);
		}
	};

	/**
	 * Creates or updates a quota row.
	 * @returns true when the row was saved, so the caller can close its form.
	 */
	const saveQuota = async (
		quota: SapCodeQuotaWriteDto,
		id?: number,
	): Promise<boolean> => {
		setSavingQuota(true);
		try {
			const quotaApi = await QuotaApiClient(instance);
			const response = id
				? await quotaApi.updateSapCodeQuota(id, quota)
				: await quotaApi.createSapCodeQuota(quota);

			// Lowering the maximum below the consumed quantity is allowed on purpose, and comes back with a
			// warning rather than an error: the row is saved and the operator has to know why nothing is left.
			if (response.data?.warning) {
				notification.warning({
					message: 'Quota saved with a warning',
					description: response.data.warning,
					duration: 6,
				});
			} else {
				notification.success({
					message: id ? 'Quota updated!' : 'Quota created!',
					description: `Quota for sap code ${quota.sapCode} saved.`,
					duration: 2,
				});
			}

			return true;
		} catch (reason: any) {
			notification.error({
				message: 'Unable to save the quota!',
				description:
					reason?.response?.data?.title ?? 'Unable to save the quota.',
				duration: 4,
			});

			return false;
		} finally {
			setSavingQuota(false);
		}
	};

	const removeQuota = async (id: number) => {
		setRemovingQuota(true);
		try {
			const quotaApi = await QuotaApiClient(instance);
			await quotaApi.deleteSapCodeQuota(id);

			notification.success({
				message: 'Quota deleted!',
				description: 'The sap code is unlimited again for that area.',
				duration: 2,
			});
		} catch (reason: any) {
			notification.error({
				message: 'Unable to delete the quota!',
				description:
					reason?.response?.data?.title ?? 'Unable to delete the quota.',
				duration: 4,
			});
		} finally {
			setRemovingQuota(false);
		}
	};

	const resetQuotaCounter = async (id: number) => {
		setResettingQuota(true);
		try {
			const quotaApi = await QuotaApiClient(instance);
			const response = await quotaApi.resetSapCodeQuotaCounter(id);

			notification.success({
				message: 'Counter reset!',
				description: `Orders confirmed before now no longer count for sap code ${response.data?.quota?.sapCode}.`,
				duration: 3,
			});
		} catch (reason: any) {
			notification.error({
				message: 'Unable to reset the counter!',
				description:
					reason?.response?.data?.title ?? 'Unable to reset the counter.',
				duration: 4,
			});
		} finally {
			setResettingQuota(false);
		}
	};

	const exportQuotas = async () => {
		setExportingQuotas(true);
		try {
			const quotaApi = await QuotaApiClient(instance);
			const response = await quotaApi.exportSapCodeQuotas();
			saveBlob(response.data, (response as any)?.headers, `quotas_export_${today()}.xlsx`);
		} catch {
			notification.error({
				message: 'Export failed',
				description: 'Unable to export the quotas.',
				duration: 2,
			});
		} finally {
			setExportingQuotas(false);
		}
	};

	const exportQuotasTemplate = async () => {
		setExportingQuotas(true);
		try {
			const quotaApi = await QuotaApiClient(instance);
			const response = await quotaApi.exportSapCodeQuotasImportTemplate();
			saveBlob(
				response.data,
				(response as any)?.headers,
				`quotas_import_template_${today()}.xlsx`,
			);
		} catch {
			notification.error({
				message: 'Download failed',
				description: 'Unable to download the import template.',
				duration: 2,
			});
		} finally {
			setExportingQuotas(false);
		}
	};

	const importQuotas = async (file: File | Blob) => {
		setImportingQuotas(true);
		setImportResult(null);
		try {
			const quotaApi = await QuotaApiClient(instance);
			const response = await quotaApi.importSapCodeQuotas(file);
			const result = response.data;

			setImportResult(result);

			// Invalid rows are reported one by one instead of failing the whole file, so the message has to say
			// both what went through and what did not.
			if (result.skipped > 0) {
				notification.warning({
					message: 'Import completed with errors',
					description: `${result.created} created, ${result.updated} updated, ${result.skipped} skipped out of ${result.totalRows} rows. See the report below.`,
					duration: 6,
				});
			} else {
				notification.success({
					message: 'Import completed',
					description: `${result.created} created, ${result.updated} updated out of ${result.totalRows} rows.`,
					duration: 3,
				});
			}
		} catch (reason: any) {
			notification.error({
				message: 'Import failed',
				description:
					reason?.response?.data?.title ?? 'Unable to import the quotas.',
				duration: 4,
			});
		} finally {
			setImportingQuotas(false);
		}
	};

	return {
		handleQuotas,
		quotas,
		quotasTotal,
		quotasLoading,
		handleAreas,
		areas,
		areasLoading,
		saveQuota,
		savingQuota,
		removeQuota,
		removingQuota,
		resetQuotaCounter,
		resettingQuota,
		exportQuotas,
		exportQuotasTemplate,
		exportingQuotas,
		importQuotas,
		importingQuotas,
		importResult,
		clearImportResult: () => setImportResult(null),
	};
};
